import { useEffect, useState } from 'react';
import { fetchDeliveryDelays } from '../services/deliveryDelayService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CHART_CARD = {
  background: 'white',
  borderRadius: 14,
  border: '1px solid #e3e8ef',
  padding: '20px 16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const COLORS = [
  '#0B3D6B','#0E4A7F','#115893','#1466A7','#1774BB',
  '#1A82CF','#1E8FE0','#239CE8','#2EAAEF','#40B5F3',
];

const truncate = (s, max = 22) => s && s.length > max ? s.slice(0, max) + '\u2026' : s;

export default function DeliveryDelays() {
  const [delays, setDelays] = useState(null);
  const [chart, setChart] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('quarters_slipped');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    fetchDeliveryDelays()
      .then((d) => {
        setDelays(d.delays);
        setChart(d.chart);
        setTotal(d.total);
      })
      .catch(() => setError('Failed to load delivery delay data'));
  }, []);

  const sorted = delays
    ? [...delays].sort((a, b) => {
        let av = a[sortCol], bv = b[sortCol];
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      })
    : null;

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'quarters_slipped' ? 'desc' : 'asc');
    }
  };

  const arrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  return (
    <div className="content">
      <div style={{ maxWidth: 1400, margin: '0 auto', width: '100%' }}>
      <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 22, color: 'var(--navy)', marginBottom: 4 }}>
        Delivery Delays
      </h1>
      <p style={{ color: 'var(--t2)', fontSize: 13, marginBottom: 24 }}>
        Molecules with a past-due dossier preparation planned date (target quarter has ended, not yet NL-available).
        {delays && <span style={{ marginLeft: 8, fontWeight: 600 }}>{total} delayed molecule{total !== 1 ? 's' : ''} found.</span>}
      </p>

      {/* Chart */}
      <div className="row g-3" style={{ marginBottom: 28 }}>
        <div className="col-lg-8 col-12">
          <div style={CHART_CARD}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 16 }}>
              Top 10 Most Delayed Suppliers
            </div>
            {error ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc3545', fontSize: 14 }}>Failed to load data</div>
            ) : !chart ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)', fontSize: 14 }}>Loading...</div>
            ) : chart.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)', fontSize: 14 }}>No delayed suppliers found</div>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={chart} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 10 }}>
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#6c757d' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="supplier_name"
                    width={170}
                    tick={{ fontSize: 12, fill: 'var(--navy)' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => truncate(v)}
                  />
                  <Tooltip
                    formatter={(val, _name, props) => [val, 'Delayed Molecules']}
                    labelFormatter={(label) => label}
                    contentStyle={{ fontSize: 13, borderRadius: 8 }}
                  />
                  <Bar dataKey="delayed_count" radius={[0, 6, 6, 0]} barSize={20}>
                    {chart.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Summary card */}
        <div className="col-lg-4 col-12">
          <div style={{ ...CHART_CARD, justifyContent: 'center', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 48, fontWeight: 700, color: 'var(--navy)', fontFamily: "'DM Sans', sans-serif" }}>
              {delays ? total : '-'}
            </div>
            <div style={{ fontSize: 14, color: 'var(--t2)' }}>Total Delayed Molecules</div>
            {delays && total > 0 && (
              <div style={{ fontSize: 13, color: 'var(--t3)', textAlign: 'center', marginTop: 4 }}>
                across {chart ? chart.length : '-'} supplier{chart && chart.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div style={{ ...CHART_CARD, padding: 40, textAlign: 'center', color: '#dc3545' }}>{error}</div>
      ) : !sorted ? (
        <div style={{ ...CHART_CARD, padding: 40, textAlign: 'center', color: 'var(--t3)' }}>Loading delays...</div>
      ) : sorted.length === 0 ? (
        <div style={{ ...CHART_CARD, padding: 40, textAlign: 'center', color: 'var(--t3)' }}>
          No delivery delays detected. Delays will appear as more supplier documents are loaded and dossier dates shift.
        </div>
      ) : (
        <div style={{ ...CHART_CARD, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-hover mb-0" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={TH} onClick={() => handleSort('supplier_name')}>Supplier{arrow('supplier_name')}</th>
                  <th style={TH} onClick={() => handleSort('inn')}>INN{arrow('inn')}</th>
                  <th style={TH}>Planned Date</th>
                  <th style={TH}>Raw Value</th>
                  <th style={TH}>Document</th>
                  <th style={{ ...TH, textAlign: 'center' }} onClick={() => handleSort('quarters_slipped')}>Quarters Late{arrow('quarters_slipped')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fffbf0' : '#fff8e6' }}>
                    <td style={TD}>{d.supplier_name}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{d.inn}</td>
                    <td style={{ ...TD, color: '#b45309', fontWeight: 600 }}>{d.planned_date_display}</td>
                    <td style={{ ...TD, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, color: 'var(--t3)' }} title={d.planned_date_raw}>{d.planned_date_raw}</td>
                    <td style={{ ...TD, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.document}>{d.document || '—'}</td>
                    <td style={{ ...TD, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        background: d.quarters_slipped >= 4 ? '#dc354520' : '#f59e0b20',
                        color: d.quarters_slipped >= 4 ? '#dc3545' : '#b45309',
                        fontWeight: 700,
                        borderRadius: 6,
                        padding: '2px 10px',
                        fontSize: 13,
                      }}>
                        +{d.quarters_slipped}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

const TH = {
  cursor: 'pointer',
  userSelect: 'none',
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--navy)',
  borderBottom: '2px solid #e3e8ef',
  whiteSpace: 'nowrap',
};

const TD = {
  padding: '8px 12px',
  verticalAlign: 'middle',
};

function fmtDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}
